import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiError, AuthService, MessageService } from '../../../core';
import { ClienteAnaliseMercadoFilter, ClienteAnaliseMercadoViewProps } from '../adapters';
import { ClienteAnaliseMercadoRevisaoSearch } from '../revisao/adapters';
import { ClienteAnaliseMercadoAuthGuardService, ClienteAnaliseMercadoRevisaoHttpService } from '../revisao/services';
import { Representante, RepresentanteCrudHttpService } from '../../pessoa/representante';
import { ClienteAnaliseMercadoQueryParams } from '../routes';

@Injectable({
  providedIn: 'root'
})
/**
 * Service para as views das Análises de Mercado
 */
export class ClienteAnaliseMercadoViewService implements OnDestroy {
    /** Props da view das Análises de Mercado */
    private props: ClienteAnaliseMercadoViewProps = {
        queryParams: null,
        anoRevisao: null,
        idRepresentanteRevisao: null,
        representanteRevisao: null,
        showRevisao: false,
        revisaoMode: false,
        revisaoAtual: null,
        filter: null
    };
    private propsSubject = new BehaviorSubject<ClienteAnaliseMercadoViewProps>(this.props);
    props$ = this.propsSubject.asObservable();

    // Load
    private loadSubject = new Subject<ClienteAnaliseMercadoFilter>();
    load$ = this.loadSubject.asObservable();


    constructor(private authService: AuthService,
                private messageService: MessageService,
                private clienteAnaliseMercadoAuthGuardService: ClienteAnaliseMercadoAuthGuardService,
                private clienteAnaliseMercadoRevisaoHttpService: ClienteAnaliseMercadoRevisaoHttpService,
                private representanteCrudHttpService: RepresentanteCrudHttpService) { }


    /** Executa o evento para carregar a view */
    execLoad(filter: ClienteAnaliseMercadoFilter) {
        this.setFilter(filter);
        this.loadSubject.next(filter);
    }

    /** Obtem as querys params do router das Análises de Mercado */
    getQueryParams(): ClienteAnaliseMercadoQueryParams {
        return this.props?.queryParams ?? null;
    }

    /** Seta as querys params do router das Análises de Mercado */
    setQueryParams(queryParams: ClienteAnaliseMercadoQueryParams) {
        this.props.queryParams = queryParams;
        this.propsSubject.next(this.props);
    }

    /** Retorna o filtro da view */
    getFilter(): ClienteAnaliseMercadoFilter {
        return this.props?.filter ?? null;
    }

    /** Seta o filtro da view */
    setFilter(filter: ClienteAnaliseMercadoFilter) {
        this.props.filter = filter;
        this.propsSubject.next(this.props);
    }

    /** Seta as props da revisão */
    setRevisao(ano: number, idsRepresentante: number[]): Promise<void> {
        return new Promise<void>(async (resolve) => {
            // Setando o ano da revisão
            this.props.anoRevisao = ano;

            if (this.clienteAnaliseMercadoAuthGuardService.canShowRevisoes(idsRepresentante)) { // Se puder exibir as revisões
                this.props.showRevisao = true;

                // Representante
                if (this.authService.isRepresentante()) { // Representante logado
                    this.props.idRepresentanteRevisao = this.authService.getIdRepresentante();
                } else if (idsRepresentante != null && idsRepresentante.length > 0) { // Representante filtrado
                    this.props.idRepresentanteRevisao = idsRepresentante[0];
                }

                // Carregando revisão atual do representante no ano
                this.props.revisaoAtual = await this.searchRevisaoAtual(ano, this.props.idRepresentanteRevisao);
            } else {
                this.props.showRevisao = false;
                this.props.idRepresentanteRevisao = null;
                this.props.revisaoAtual = null;
            }

            this.propsSubject.next(this.props);
            resolve();
        })
    }

    /** Seta o representante da revisão */
    async setRepresentanteRevisao() {
        // Se possuir o representante na revisão
        if (this.props.idRepresentanteRevisao) {
            this.props.representanteRevisao = await this.findRepresentanteRevisao(this.props.idRepresentanteRevisao);
        } else {
            this.props.representanteRevisao = null;
        }
    }

    /** Seta se a view está ou não em modo de revisão */
    setRevisaoMode(revisaoMode: boolean) {
        this.props.revisaoMode = revisaoMode;
        this.propsSubject.next(this.props);
    }

    /** Seta se a view está ou não em modo de revisão */
    setRevisaoAtual(revisaoAtual: ClienteAnaliseMercadoRevisaoSearch) {
        this.props.revisaoAtual = revisaoAtual;
        this.propsSubject.next(this.props);
    }

    /** Carrega o representante selecionado para a revisão */
    private findRepresentanteRevisao(idRepresentante: number): Promise<Representante> {
        return new Promise<Representante>((resolve, reject) => {
            this.representanteCrudHttpService.find(idRepresentante)
                .pipe(take(1))
                .subscribe(
                    representante => resolve(representante),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o representante para a revisão das Análises de Mercado');
                        reject(error);
                    }
                );
        });
    }

    /** Pesquisa a revisão atual das Análises de Mercados de um representante em um ano */
    private searchRevisaoAtual(ano: number, idRepresentante: number): Promise<ClienteAnaliseMercadoRevisaoSearch> {
        return new Promise<ClienteAnaliseMercadoRevisaoSearch>((resolve, reject) => {
            this.clienteAnaliseMercadoRevisaoHttpService.searchAtual(ano, idRepresentante)
                .pipe(take(1))
                .subscribe(
                    (revisao) => resolve(revisao),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar a revisão atual das Análises de Mercado', error),
                        reject(error);
                    }
                );
        })
    }

    ngOnDestroy(): void {
        this.loadSubject.unsubscribe();
        this.propsSubject.unsubscribe();
    }
}
